import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, TrendingUp, Pencil, ChevronRight } from 'lucide-react';
import TabTip from './ui/TabTip';
import { fetchWithAuth } from '../utils/api';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PortfolioCompleteness = ({ onEditInvestment }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [expandedField, setExpandedField] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/portfolio/completeness`);
      if (res.ok) {
        setData(await res.json());
      }
    } catch (e) {
      logger.error('Completeness check error:', e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data || data.total_investments === 0) return null;

  const pct = data.completeness_percent;
  const getThresholdClass = (high, mid, low) => {
    if (pct >= 80) return high;
    if (pct >= 50) return mid;
    return low;
  };
  const color = getThresholdClass('text-emerald-600', 'text-amber-600', 'text-red-600');
  const barColor = getThresholdClass('bg-emerald-500', 'bg-amber-500', 'bg-red-500');

  return (
    <Card className="border-slate-200" data-testid="portfolio-completeness">
      <CardContent className="p-4">
        <button
          type="button"
          className="w-full flex items-center justify-between"
          onClick={() => setExpanded(!expanded)}
          data-testid="completeness-toggle"
        >
          <div className="flex items-center gap-3">
            <TrendingUp className={`w-5 h-5 ${color}`} />
            <div className="text-left">
              <p className="text-sm font-semibold text-slate-900">
                Portfolio Completeness: <span className={color}>{pct}%</span><TabTip tip="portfolio_completeness" showIcon={true} />
              </p>
              <p className="text-xs text-slate-500">
                {data.filled_fields} of {data.total_fields} data fields populated across {data.total_investments} investments
              </p>
            </div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>

        <div className="mt-3">
          <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
            <div className={`h-2 rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
          </div>
        </div>

        {expanded && data.breakdown?.length > 0 && (
          <div className="mt-4 space-y-1">
            <p className="text-xs font-semibold text-slate-600 mb-2">Missing fields (click to see investments)</p>
            {data.breakdown.map(item => (
              <div key={item.field}>
                <button
                  type="button"
                  className="w-full flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-slate-50 transition"
                  onClick={() => setExpandedField(expandedField === item.field ? null : item.field)}
                  data-testid={`completeness-field-${item.field.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <span className="text-slate-700 flex items-center gap-1">
                    <ChevronRight className={`w-3 h-3 text-slate-400 transition-transform ${expandedField === item.field ? 'rotate-90' : ''}`} />
                    {item.field}
                  </span>
                  <span className="text-slate-500">{item.missing_count} investment{item.missing_count !== 1 ? 's' : ''}</span>
                </button>

                {expandedField === item.field && item.investments?.length > 0 && (
                  <div className="ml-5 mt-1 mb-2 space-y-1" data-testid={`completeness-investments-${item.field.replace(/\s+/g, '-').toLowerCase()}`}>
                    {item.investments.map(inv => (
                      <div key={inv.investment_id} className="flex items-center justify-between py-1 px-2 bg-slate-50 rounded text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-slate-800 font-medium truncate">{inv.name}</span>
                          <span className="text-slate-400 shrink-0">({inv.type.replace(/_/g, ' ')})</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 shrink-0"
                          onClick={() => onEditInvestment?.(inv.investment_id)}
                          data-testid={`edit-incomplete-${inv.investment_id}`}
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Update
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PortfolioCompleteness;
